import { useEffect, useRef, type RefObject } from 'react';

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const DEFAULT_MODAL_LAYER_SELECTOR = '[data-modal-layer]';
const DEFAULT_ROOT_ID = 'root';

let bodyScrollLockCount = 0;
let bodyOverflowBeforeDialog = '';

type FocusTrapLayer = {
  id: symbol;
  dialog: HTMLElement;
};

const focusTrapLayerStack: FocusTrapLayer[] = [];

function registerActiveFocusLayer(dialog: HTMLElement): () => void {
  const entry: FocusTrapLayer = { dialog, id: Symbol('focus-trap-layer') };
  focusTrapLayerStack.push(entry);

  return () => {
    const index = focusTrapLayerStack.findIndex((layer) => layer.id === entry.id);
    if (index === -1) return;
    focusTrapLayerStack.splice(index, 1);
  };
}

function isTopActiveLayer(dialog: HTMLElement): boolean {
  const top = focusTrapLayerStack.at(-1);
  return top?.dialog === dialog;
}

function lockBodyScroll(): () => void {
  if (bodyScrollLockCount === 0) {
    bodyOverflowBeforeDialog = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyScrollLockCount += 1;

  return () => {
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0) document.body.style.overflow = bodyOverflowBeforeDialog;
  };
}

function visibleFocusableElements(dialog: HTMLElement): HTMLElement[] {
  return [...dialog.querySelectorAll<HTMLElement>(focusableSelector)].filter(
    (element) =>
      !element.closest('[aria-hidden="true"], [inert]') &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== 'hidden',
  );
}

function isolateRootElement(root: HTMLElement): () => void {
  const inert = root.inert;
  const ariaHidden = root.getAttribute('aria-hidden');
  root.inert = true;
  root.setAttribute('aria-hidden', 'true');

  return () => {
    root.inert = inert;
    if (ariaHidden === null) root.removeAttribute('aria-hidden');
    else root.setAttribute('aria-hidden', ariaHidden);
  };
}

function isolateDialog(
  dialog: HTMLElement,
  options: {
    focusRootId: string;
    isolationIgnoreSelector?: string;
    modalLayerSelector: string;
  },
): () => void {
  const layer = dialog.closest<HTMLElement>(options.modalLayerSelector) ?? dialog;
  const parent = layer.parentElement;
  const rootScope = document.getElementById(options.focusRootId) ?? null;

  if (parent === document.body && rootScope && rootScope !== layer) {
    return isolateRootElement(rootScope);
  }
  // Portalled dialogs must have an explicit application root.  Falling back to
  // body siblings hides unrelated system portals (toasts, widgets, and tools)
  // and cannot restore nodes that mount while the dialog is open.
  if (parent === document.body) return () => undefined;
  if (!parent) return () => undefined;

  const previousStates = [...parent.children]
    .filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        element !== layer &&
        (!options.isolationIgnoreSelector || !element.matches(options.isolationIgnoreSelector)),
    )
    .map((element) => ({
      ariaHidden: element.getAttribute('aria-hidden'),
      element,
      inert: element.inert,
    }));

  for (const { element } of previousStates) {
    element.inert = true;
    element.setAttribute('aria-hidden', 'true');
  }

  return () => {
    for (const { ariaHidden, element, inert } of previousStates) {
      element.inert = inert;
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    }
  };
}

type ReturnFocusMode = 'always' | 'if-detached';

export interface UseDialogFocusTrapOptions {
  active?: boolean;
  closeOnEscape?: boolean;
  focusRootId?: string;
  isolationIgnoreSelector?: string;
  modalLayerSelector?: string;
  returnFocusMode?: ReturnFocusMode;
}

export function useDialogFocusTrap<T extends HTMLElement>(
  dialogRef: RefObject<T | null>,
  onClose: () => void,
  {
    active = true,
    closeOnEscape = true,
    focusRootId = DEFAULT_ROOT_ID,
    isolationIgnoreSelector,
    modalLayerSelector = DEFAULT_MODAL_LAYER_SELECTOR,
    returnFocusMode = 'always',
  }: UseDialogFocusTrapOptions = {},
): void {
  const onCloseRef = useRef(onClose);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!active) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const unlockBodyScroll = lockBodyScroll();
    const restoreIsolation = isolateDialog(dialog, {
      focusRootId,
      modalLayerSelector,
      ...(isolationIgnoreSelector ? { isolationIgnoreSelector } : {}),
    });
    const unregisterLayer = registerActiveFocusLayer(dialog);
    const frame = window.requestAnimationFrame(() => {
      const autofocus = dialog.querySelector<HTMLElement>('[autofocus]');
      (autofocus ?? visibleFocusableElements(dialog)[0] ?? dialog).focus({ preventScroll: true });
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopActiveLayer(dialog)) return;
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = visibleFocusableElements(dialogRef.current);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      unregisterLayer();
      restoreIsolation();
      unlockBodyScroll();

      const returnFocus = returnFocusRef.current;
      if (!returnFocus || !returnFocus.isConnected) return;
      if (returnFocusMode === 'if-detached' && dialogRef.current?.isConnected) return;

      window.requestAnimationFrame(() => {
        if (!returnFocus.isConnected) return;
        returnFocus.focus({ preventScroll: true });
      });
    };
  }, [
    active,
    closeOnEscape,
    dialogRef,
    focusRootId,
    isolationIgnoreSelector,
    modalLayerSelector,
    returnFocusMode,
  ]);
}
